import React, { useState, useEffect, useRef } from 'react';
import eventPlanningDropdowns from '../data/eventplanningdropdowns.json';
import locationAreaInfo from '../data/locationareainfo.json';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const EventsPlanning = () => {
  // State management
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [locationname, setLocationname] = useState('');
  const [businesstype, setBusinesstype] = useState('');
  const [areaname, setAreaname] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAreaSuggestions, setShowAreaSuggestions] = useState(false);
  // const [showToast, setShowToast] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Ref for horizontally scrolling featured row
  const featuredScrollRef = useRef(null);
  
  const scrollFeaturedBy = (delta) => {
    if (featuredScrollRef.current) {
      featuredScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  // Modal handlers
  const handleViewDetails = (item) => {
    setModalItem(item);
    const photos = (item.photos && item.photos.length > 0) ? item.photos : [];
    setSelectedPhoto(photos[0] || null);
  };

  const closeModal = () => {
    setModalItem(null);
    setSelectedPhoto(null);
  };

  const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

  // Fetch data from MySQL backend
  useEffect(() => {
    const fetchListings = async () => {
      if (!API_BASE) {
        setLoading(false);
        setError('API base URL is not configured. Please set REACT_APP_API_BASE in .env.');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE}/api/eventplanning`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received from API');
        }
        // Parse photos field if it's a string (comma-separated or JSON)
        const parsePhotos = (photos) => {
          if (Array.isArray(photos)) return photos;
          if (typeof photos === 'string') {
            // Try JSON parse first
            try {
              const arr = JSON.parse(photos);
              if (Array.isArray(arr)) return arr;
            } catch {}
            // Fallback: comma-separated
            return photos.split(',').map(s => s.trim()).filter(Boolean);
          }
          return [];
        };
        // Filter active listings and sort by listingorder (property is listingorder, not listingOrder)
        const activeListings = data
          .filter(item => item.accountstatus === 'A')
          .map(item => ({ ...item, photos: parsePhotos(item.photos) }))
          .sort((a, b) => (a.listingorder || 0) - (b.listingorder || 0));
        setListings(activeListings);
      } catch (err) {
        console.error('Error fetching event planning listings:', err);
        setError(err.message || 'Failed to fetch event planning listings');
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [API_BASE]);

  // Filter options from listings
  const locationOptions = (locationAreaInfo?.locationNames || []).slice().sort((a, b) => {
    const orderA = Number.isFinite(Number(a.listorder)) ? Number(a.listorder) : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(Number(b.listorder)) ? Number(b.listorder) : Number.MAX_SAFE_INTEGER;
    return orderA - orderB || a.label.localeCompare(b.label);
  });
  const areaOptions = (locationAreaInfo?.areaNames || []).slice().sort((a, b) => a.label.localeCompare(b.label));
  const businessTypeOptions = (eventPlanningDropdowns?.businessTypes || []).slice().sort((a, b) => {
    const orderA = Number.isFinite(Number(a.listorder)) ? Number(a.listorder) : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(Number(b.listorder)) ? Number(b.listorder) : Number.MAX_SAFE_INTEGER;
    return orderA - orderB || a.label.localeCompare(b.label);
  });

  // Debugging logs for dropdown options
  console.log('Listings:', listings);
  console.log('Location Options:', locationOptions);
  console.log('Area Options:', areaOptions);
  console.log('Business Type Options:', businessTypeOptions);

  // Get name suggestions based on search input
  const suggestions = search.length > 0
    ? Array.from(new Set(
        listings
          .filter(i => i.businessname && i.businessname.toLowerCase().includes(search.toLowerCase()))
          .map(i => i.businessname)
      )).slice(0, 8)
    : [];

  const areaSearchText = String(areaname || '').trim().toLowerCase();
  const areaSuggestions = areaOptions
    .filter((area) => {
      if (!areaSearchText) return true;
      return String(area.label || '').toLowerCase().includes(areaSearchText)
        || String(area.value || '').toLowerCase().includes(areaSearchText);
    })
    .slice(0, 12);

  // Filter listings based on search and filters
  const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase();

  let filteredListings = listings;
  if (locationname !== '') {
    filteredListings = filteredListings.filter(item => normalizeFilterValue(item.locationname) === normalizeFilterValue(locationname));
  }
  if (areaname !== '') {
    filteredListings = filteredListings.filter(item => normalizeFilterValue(item.areaname).includes(normalizeFilterValue(areaname)));
  }
  if (businesstype !== '') {
    filteredListings = filteredListings.filter(item => normalizeFilterValue(item.businesstype) === normalizeFilterValue(businesstype));
  }
  if (search.trim() !== '') {
    filteredListings = filteredListings.filter(item =>
      (item.businessname && item.businessname.toLowerCase().includes(search.trim().toLowerCase())) ||
      (item.title && item.title.toLowerCase().includes(search.trim().toLowerCase())) ||
      (item.aboutdescription && item.aboutdescription.toLowerCase().includes(search.trim().toLowerCase()))
    );
  }

  // Featured listings
  const featuredListings = listings.filter(item => item.featuredlisting === 1 || item.featuredlisting === true);


  // Render loading state
  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 18, color: '#1976d2', marginBottom: 10 }}>Loading Event Planning listings...</div>
        <div style={{ fontSize: 14, color: '#666' }}>Fetching data from database...</div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ background: '#ffebee', border: '1px solid #ef5350', borderRadius: 8, padding: 16 }}>
          <h2 style={{ color: '#c62828', margin: '0 0 10px 0', fontSize: 18 }}>Unable to connect to remote server</h2>
          <p style={{ margin: '8px 0', color: '#333' }}><strong>Error:</strong> {error}</p>
          <div style={{ marginTop: 12, padding: 12, background: '#fff3cd', borderLeft: '4px solid #ffc107', fontSize: 14 }}>
            <strong>Troubleshooting:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>Make sure the backend server is running on port 5000</li>
              <li>Check if MySQL database is running</li>
              <li>Verify the EventPlanning table exists and has data</li>
              <li>Check your network connection</li>
            </ul>
            If you're on mobile, ensure your phone and PC are on the same Wi‑Fi and that the API is reachable at <code>{API_BASE + '/api/eventplanning'}</code>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
  <header style={{ background: '#e8edf3ff', color: '#37864fff', padding: '0.25rem', textAlign: 'center', borderRadius: 8, marginBottom: 2 }}>
        <h1 style={{ margin: 0, fontSize: '1.0rem', fontWeight: 700 }}>Event Planning</h1>
      </header>

      {/* Featured Section */}
      {featuredListings.length > 0 && (
        <div style={{ marginBottom: '0.2rem', position: 'relative' }}>
          
          {/* Left scroll button */}
          <button
            aria-label="Scroll featured left"
            onClick={() => scrollFeaturedBy(-250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              left: 0,
              zIndex: 2,
              background: 'rgba(25, 118, 210, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ‹
          </button>

          {/* Featured scroll container */}
          <div
            ref={featuredScrollRef}
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: '4px 44px',
              scrollSnapType: 'x mandatory',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {featuredListings.map((item) => (
              <div
                key={item.bid}
                onClick={() => handleViewDetails(item)}
                style={{
                  minWidth: 120,
                  maxWidth: 150,
                  height: 180,
                  background: '#fff',
                  border: '0.25px solid #1976d2',
                  borderRadius: 12,
                  padding: 6,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  scrollSnapAlign: 'start',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <img
                  src={item.imagepath || placeholderImg}
                  alt={item.businessname}
                  style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }}
                  onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                />
                <h3 style={{ margin: 0, fontSize: 13, color: '#1976d2' }}>{item.businessname}</h3>
                {item.businesstype && <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{item.businesstype}</div>}
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 500 }}>{item.locationname} • {item.areaname}</div>
                {/* Social media icons */}
                {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {item.sm_instagram && (
                      <a
                        href={`https://instagram.com/${item.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {item.sm_facebook && (
                      <a
                        href={`https://facebook.com/${item.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {item.sm_youtube && (
                      <a
                        href={`https://youtube.com/${item.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="YouTube"
                      >
                        ▶
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <button
            aria-label="Scroll featured right"
            onClick={() => scrollFeaturedBy(250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              right: 0,
              zIndex: 2,
              background: 'rgba(25, 118, 210, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{ marginBottom: '1rem', marginTop: '0.2rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Search bar with suggestions */}
        <div style={{ position: 'relative', width: '70%' }}>
          <input
            type="text"
            placeholder="Search by business name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #ccc',
              borderRadius: 8,
              boxSizing: 'border-box'
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: '1px solid #ccc',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              maxHeight: 200,
              overflowY: 'auto',
              zIndex: 10,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              {suggestions.map((name, idx) => (
                <div
                  key={idx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSearch(name);
                    setShowSuggestions(false);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setSearch(name);
                    setShowSuggestions(false);
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: 14,
                    borderBottom: idx < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                    background: '#fff'
                  }}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filter dropdowns */}
        <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          <select
            value={locationname}
            onChange={(e) => setLocationname(e.target.value)}
            style={{ flex: 1, minWidth: 100, padding: '6px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6 }}
          >
            <option value="">All Locations</option>
            {locationOptions.map(loc => <option key={loc.id} value={loc.value}>{loc.label}</option>)}
          </select>

          <div style={{ flex: 1, minWidth: 100, position: 'relative' }}>
            <input
              type="text"
              value={areaname}
              onChange={(e) => setAreaname(e.target.value)}
              onFocus={() => setShowAreaSuggestions(true)}
              onBlur={() => setTimeout(() => setShowAreaSuggestions(false), 200)}
              placeholder="All Areas"
              aria-label="Area"
              style={{ width: '100%', padding: '6px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' }}
            />

            {showAreaSuggestions && areaSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #ccc',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                maxHeight: 220,
                overflowY: 'auto',
                zIndex: 10,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {areaSuggestions.map((ar, idx) => (
                  <div
                    key={ar.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setAreaname(ar.value);
                      setShowAreaSuggestions(false);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setAreaname(ar.value);
                      setShowAreaSuggestions(false);
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: 14,
                      borderBottom: idx < areaSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: '#fff'
                    }}
                  >
                    {ar.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <select
            value={businesstype}
            onChange={(e) => setBusinesstype(e.target.value)}
            style={{ flex: 1, minWidth: 100, padding: '6px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6 }}
          >
            <option value="">All Types</option>
            {businessTypeOptions.map(tp => <option key={tp.id} value={tp.value}>{tp.label}</option>)}
          </select>
        </div>

        {/* Active filters indicator */}
        {(search || locationname || areaname || businesstype) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: '#666' }}>Active filters:</span>
            {search && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Search: {search}</span>}
            {locationname && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Location: {locationname}</span>}
            {areaname && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Area: {areaname}</span>}
            {businesstype && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Type: {businesstype}</span>}
            <button
              onClick={() => { setSearch(''); setLocationname(''); setAreaname(''); setBusinesstype(''); }}
              style={{ background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '0.3rem', marginTop: '-0.5rem', fontSize: 14, color: '#666' }}>
        {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'} found
      </div>

      {/* Listings Grid */}
      {filteredListings.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No listings found</div>
          <div style={{ fontSize: 14 }}>Try adjusting your search or filters</div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 4,
          marginBottom: 20
        }}>
          {filteredListings.map((item) => (
            <div
              key={item.bid}
              onClick={() => handleViewDetails(item)}
              style={{
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                padding: 12,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
              }}
            >
              <img
                src={item.imagepath || placeholderImg}
                alt={item.businessname}
                style={{ width: '100%', height: 240, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }}
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
              />
              <h3 style={{ margin: '2px 0', fontSize: 12, color: '#1976d2' }}>{item.businessname}</h3>
              {item.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>{item.businesstype}</div>}
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8, fontWeight: 600 }}>{item.locationname} • {item.areaname}</div>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: 14,
                color: '#555',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {item.aboutdescription}
              </p>
              {/* Social media icons */}
              {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {item.sm_instagram && (
                    <a
                      href={`https://instagram.com/${item.sm_instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Instagram"
                    >
                      IG
                    </a>
                  )}
                  {item.sm_facebook && (
                    <a
                      href={`https://facebook.com/${item.sm_facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: '#1877F2',
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Facebook"
                    >
                      f
                    </a>
                  )}
                  {item.sm_youtube && (
                    <a
                      href={`https://youtube.com/${item.sm_youtube}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: '#FF0000',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="YouTube"
                    >
                      ▶
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal for item details */}
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 450,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                cursor: 'pointer',
                fontSize: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}
            >
              ×
            </button>

            {/* Modal content: Main image with multiple images and scroll buttons */}
            <div style={{ position: 'relative', marginBottom: 3, width: '100%', maxWidth: 400, margin: '12px auto 0 auto' }}>
              {(() => {
                const photos = (modalItem.photos && modalItem.photos.length > 0) ? modalItem.photos : [];
                if (photos.length === 0) {
                  return (
                    <img
                      src={placeholderImg}
                      alt="No images available"
                      style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 12, display: 'block' }}
                    />
                  );
                }
                return (
                  <>
                    <img
                      src={selectedPhoto || photos[0] || placeholderImg}
                      alt={modalItem.businessname}
                      style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 12, display: 'block' }}
                      onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
                    />
                    {/* Prev/Next buttons for main image */}
                    {photos.length > 1 && (
                      <>
                        <button
                          aria-label="Previous image"
                          onClick={() => {
                            const current = photos.indexOf(selectedPhoto || photos[0]);
                            const prevIndex = (current - 1 + photos.length) % photos.length;
                            setSelectedPhoto(photos[prevIndex]);
                          }}
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(25,118,210,0.95)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 20,
                            width: 36,
                            height: 36,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                            transition: 'background 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                            zIndex: 2
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        <button
                          aria-label="Next image"
                          onClick={() => {
                            const current = photos.indexOf(selectedPhoto || photos[0]);
                            const nextIndex = (current + 1) % photos.length;
                            setSelectedPhoto(photos[nextIndex]);
                          }}
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(25,118,210,0.95)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 20,
                            width: 36,
                            height: 36,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                            transition: 'background 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                            zIndex: 2
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Thumbnails strip (scrollable horizontally) with scroll buttons */}
            {(() => {
              const photos = (modalItem.photos && modalItem.photos.length > 0) ? modalItem.photos : [];
              return photos.length > 0 ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <button
                    onClick={() => {
                      const container = document.getElementById('eventplanning-photo-scroll-container');
                      if (container) container.scrollBy({ left: -100, behavior: 'smooth' });
                    }}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      zIndex: 2,
                    }}
                    aria-label="Scroll left"
                  >
                    ‹
                  </button>

                  <div
                    id="eventplanning-photo-scroll-container"
                    style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollBehavior: 'smooth', padding: '0 40px' }}
                  >
                    {photos.map((p, i) => (
                      <img
                        key={i}
                        src={p || placeholderImg}
                        alt={`${modalItem.businessname || modalItem.title} ${i + 1}`}
                        onClick={() => setSelectedPhoto(p)}
                        onKeyDown={e => { if (e.key === 'Enter') setSelectedPhoto(p); }}
                        tabIndex={0}
                        style={{
                          width: 96,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 6,
                          cursor: 'pointer',
                          border: (selectedPhoto || photos[0]) === p ? '3px solid #1976d2' : '2px solid rgba(0,0,0,0.08)'
                        }}
                        onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const container = document.getElementById('eventplanning-photo-scroll-container');
                      if (container) container.scrollBy({ left: 100, behavior: 'smooth' });
                    }}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      zIndex: 2,
                    }}
                    aria-label="Scroll right"
                  >
                    ›
                  </button>
                </div>
              ) : null;
            })()}
            
            <div style={{ padding: '8px 20px 20px 20px' }}>
              {modalItem.featuredlisting && (
                <div style={{ fontSize: 11, color: '#fff', background: '#388e3c', padding: '4px 10px', borderRadius: 4, display: 'inline-block', marginBottom: 6, fontWeight: 600 }}>
                  Featured
                </div>
              )}
              
              <h2 style={{ margin: '0 0 2px 0', fontSize: 18, color: '#1976d2' }}>{modalItem.title}</h2>
              {modalItem.businesstype && (
                <div style={{ fontSize: 14, color: '#888', margin: '0 0 4px 0', fontWeight: 700 }}>{modalItem.businesstype}</div>
              )}
              <div style={{ fontSize: 14, color: '#888', marginBottom: 10, fontWeight: 600 }}>
                📍 {modalItem.locationname} • {modalItem.areaname}
              </div>
              
              
              <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalItem.aboutdescription}</span>
                </div>
              
              {modalItem.contactnumber && (
                <div style={{ marginBottom: 6 }}>
                  <strong style={{ fontSize: 15, color: '#333' }}>Contact Details:</strong>
                  {modalItem.contactperson && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4 }}>
                      Name: <span style={{ fontWeight: 600, color: '#555' }}>{modalItem.contactperson}</span>
                    </div>
                  )}
                  <a 
                    href={`tel:${(modalItem.contactnumber || '').replace(/\s/g, '')}`}
                    style={{ 
                      fontSize: 13, 
                      color: '#d32f2f', 
                      marginTop: 4, 
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'block',
                      cursor: 'pointer'
                    }}
                  >
                    📞 {modalItem.contactnumber}
                  </a>
                </div>
              )}

              {modalItem.emailid && (
                <div style={{ marginBottom: 2 }}>
                  <strong style={{ fontSize: 14, color: '#333' }}>Email:</strong>
                  <a href={`mailto:${modalItem.emailid}`} style={{ fontSize: 14, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.emailid}</a>
                </div>
              )}


              {(modalItem.address1 || modalItem.website) && (
                <>
                  {modalItem.address1 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 14, color: '#333' }}>Address:</strong>
                        <span style={{ fontSize: 14, color: '#666' }}>
                          {modalItem.address1}
                          {modalItem.address1 && (
                            <>
                              {' '}
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(modalItem.address1)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline', marginLeft: 6, fontSize: 13 }}
                              >
                                [Map]
                              </a>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {modalItem.website && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4, marginBottom: 12 }}>
                      Website: {modalItem.website ? (
                        <a
                          href={modalItem.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: 400, color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
                        >
                          {modalItem.website}
                        </a>
                      ) : (
                        <span style={{ fontWeight: 400, color: '#555' }}>{modalItem.website}</span>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {/* Action buttons (match Services modal) */}
              <div style={{ display: 'flex', gap: 12, marginTop: 12, marginBottom: 8, justifyContent: 'center' }}>
                {/* Call Now button */}
                {modalItem.contactnumber && (
                  <button
                    onClick={() => window.location.href = `tel:${(modalItem.contactnumber || '').replace(/\s/g, '')}`}
                    style={{
                      padding: '8px 16px',
                      background: '#4CAF50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
                  >
                    📞 Call
                  </button>
                )}

                {/* Share button with dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    style={{
                      padding: '8px 16px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    � Share
                  </button>

                  {/* Share dropdown menu */}
                  {showShareMenu && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      {/* WhatsApp option (default) */}
                      <button
                        onClick={() => {
                          const shareText = `*${modalItem.businessname || modalItem.title}*\n${modalItem.businesstype || ''}\n📍 ${modalItem.locationname || ''}${modalItem.areaname ? ` • ${modalItem.areaname}` : ''}\n📞 ${modalItem.contactnumber || ''}${modalItem.emailid ? `\n✉️ ${modalItem.emailid}` : ''}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                          window.open(whatsappUrl, '_blank');
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#25D366',
                          color: '#fff',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#20ba5a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#25D366'}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>

                      {/* Email option */}
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`Check out ${modalItem.businessname || modalItem.title}`);
                          const body = encodeURIComponent(
                            `${modalItem.businessname || modalItem.title}\n${modalItem.businesstype || ''}\n\nLocation: ${modalItem.locationname || ''}${modalItem.areaname ? ` • ${modalItem.areaname}` : ''}\n${modalItem.address1 ? `Address: ${modalItem.address1}\n` : ''}Contact: ${modalItem.contactnumber || ''}\n${modalItem.emailid ? `Email: ${modalItem.emailid}\n` : ''}${modalItem.aboutdescription ? `\nAbout:\n${modalItem.aboutdescription}` : ''}`
                          );
                          window.location.href = `mailto:?subject=${subject}&body=${body}`;
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        ✉️
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Social media links */}
              {(modalItem.sm_instagram || modalItem.sm_facebook || modalItem.sm_youtube || modalItem.sm_twitter || modalItem.sm_linkedin) && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong style={{ fontSize: 14, color: '#333' }}>Follow us:</strong>
                    <div style={{ display: 'flex', gap: 10 }}>
                    {modalItem.sm_instagram && (
                      <a
                        href={`https://instagram.com/${modalItem.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="Instagram"
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        IG
                      </a>
                    )}
                    {modalItem.sm_facebook && (
                      <a
                        href={`https://facebook.com/${modalItem.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="Facebook"
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        f
                      </a>
                    )}
                    {modalItem.sm_youtube && (
                      <a
                        href={`https://youtube.com/${modalItem.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="YouTube"
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        ▶
                      </a>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast notification removed (showToast is undefined) */}
    </div>
  );
};

export default EventsPlanning;
